/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useTheme } from "../../../context/ThemeProvider";
import { FaPlus, FaRegUser } from "react-icons/fa6";
import { IoCall, IoChevronBack } from "react-icons/io5";
import { SlLocationPin } from "react-icons/sl";
import { useNavigate } from "react-router-dom";
import ReusableProfileCard from "./ReusableProfileCard";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { useState } from "react";
import {
  updateSpesificUserData,
  updateUserData,
} from "../../../store/Slices/UserSlice";
import useApiPost from "../../../hooks/PostData";
import toast from "react-hot-toast";
import { ClipLoader } from "react-spinners";
import { MdOutlineMailOutline } from "react-icons/md";
import { BsFillPatchCheckFill } from "react-icons/bs";
import { updateViewState } from "../../../store/Slices/ViewManagerSlice";
import TextTranslate from "../../../utils/TextTranslate";
import { UserDataRes } from "../../../types/UserDataType";
import { formatPhoneDisplay } from "../../../utils/formatPhoneDisplay";

const groupClass =
  "overflow-hidden rounded-2xl bg-primary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]";
const divide =
  "[&>*+*]:border-t [&>*+*]:border-black/[0.06] dark:[&>*+*]:border-white/[0.08]";

export default function Profile() {
  // @ts-ignore
  const { theme } = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userData = useAppSelector((state) => state.userData);
  const { loading, postData } = useApiPost();
  const [isValueChanged, setIsValueChanged] = useState(false);

  const phone = formatPhoneDisplay(userData.country_code, userData.mobile_num);

  async function updateUserDetails() {
    const userDetails: UserDataRes = await postData(
      "users/updateUser",
      userData,
    );
    dispatch(updateUserData(userDetails.data));
    toast.success("Profile Updated", { position: "bottom-left" });
    setIsValueChanged(false);
  }

  return (
    <div className="relative flex h-[100dvh] w-full max-w-md flex-col overflow-hidden bg-secondary pb-[calc(3.25rem+env(safe-area-inset-bottom))] shadow-inner lg:pb-0">
      <div className="sticky top-0 z-20 shrink-0 border-b border-black/[0.06] bg-secondary/85 backdrop-blur-xl dark:border-white/[0.08]">
        <div className="flex items-center justify-between px-3 py-3">
          <button
            type="button"
            aria-label="Back"
            className="grid h-9 w-9 place-content-center rounded-full bg-black/[0.06] text-darkText transition active:scale-95 dark:bg-white/10 lg:invisible"
            onClick={() => navigate(-1)}
          >
            <IoChevronBack className="text-xl" />
          </button>
          <span className="text-[17px] font-semibold tracking-tight text-darkText">
            <TextTranslate text="Profile" />
          </span>
          {isValueChanged ? (
            <button
              type="button"
              disabled={loading}
              onClick={() => void updateUserDetails()}
              className="min-w-9 px-1 text-[15px] font-semibold text-themeColor disabled:opacity-50"
            >
              {loading ? (
                <ClipLoader
                  size={16}
                  color={theme == "dark" ? "white" : "black"}
                />
              ) : (
                <TextTranslate text="Update" />
              )}
            </button>
          ) : (
            <div className="h-9 w-9" aria-hidden />
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-10 pt-4">
        <div className="mb-5 flex flex-col items-center text-center">
          <div className="relative mb-3">
            <img
              src={userData.profile_pic}
              className="h-[7.25rem] w-[7.25rem] rounded-full object-cover shadow-sm ring-1 ring-black/5 dark:ring-white/10"
              alt=""
            />
            <button
              type="button"
              onClick={() =>
                dispatch(updateViewState({ showChangeProfileModal: true }))
              }
              className="absolute bottom-0 right-0 grid h-8 w-8 place-content-center rounded-full bg-primary shadow ring-1 ring-black/10 dark:ring-white/15"
            >
              <FaPlus className="text-sm text-themeColor" />
            </button>
          </div>
          <div className="text-[1.375rem] font-semibold tracking-tight text-darkText">
            {userData.full_name}
          </div>
          {phone ? (
            <div className="mt-1 text-[13px] tabular-nums text-lightText">
              {phone}
            </div>
          ) : null}
        </div>

        <div className="space-y-3">
          <div className={groupClass}>
            <ReusableProfileCard
              icon={<FaRegUser className="text-lg" />}
              value={userData.full_name}
              onChange={(newValue: string) => {
                dispatch(updateSpesificUserData({ first_name: newValue }));
                if (!isValueChanged) setIsValueChanged(true);
              }}
              hideRightArrow
            />
          </div>

          <div className={`${groupClass} px-4 py-3`}>
            <div className="mb-2 text-[13px] font-medium uppercase tracking-wide text-lightText">
              <TextTranslate text="Gender" />
            </div>
            <div className="grid grid-cols-2 gap-1 rounded-[9px] bg-black/[0.06] p-[3px] dark:bg-white/[0.1]">
              {(["male", "female"] as const).map((g) => {
                const active = userData.gender === g;
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => {
                      dispatch(updateSpesificUserData({ gender: g }));
                      if (!isValueChanged) setIsValueChanged(true);
                    }}
                    className={`rounded-[7px] py-1.5 text-center text-[13px] font-semibold transition ${
                      active
                        ? "bg-primary text-darkText shadow-sm"
                        : "text-darkText/55"
                    }`}
                  >
                    <TextTranslate text={g === "male" ? "Male" : "Female"} />
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="mb-1.5 px-1 text-[13px] font-medium uppercase tracking-wide text-lightText">
              <TextTranslate text="Contact Details" />
              <span className="ml-1 normal-case text-[11px] text-[#FF3B30]">
                (<TextTranslate text="Non Changeable" />)
              </span>
            </div>
            <div className={`${groupClass} ${divide}`}>
              {userData.email ? (
                <ReusableProfileCard
                  icon={<MdOutlineMailOutline className="text-xl" />}
                  right_icon={
                    <BsFillPatchCheckFill className="text-base text-emerald-500" />
                  }
                  value={userData.email}
                  isDisabled
                />
              ) : null}
              {phone ? (
                <ReusableProfileCard
                  icon={<IoCall className="text-lg" />}
                  right_icon={
                    <BsFillPatchCheckFill className="text-base text-emerald-500" />
                  }
                  value={phone}
                  isDisabled
                />
              ) : null}
              {userData.country ? (
                <ReusableProfileCard
                  icon={<SlLocationPin className="text-lg" />}
                  right_icon={
                    <BsFillPatchCheckFill className="text-base text-emerald-500" />
                  }
                  value={userData.country}
                  isDisabled
                />
              ) : null}
              {userData.user_name ? (
                <ReusableProfileCard
                  icon={<FaRegUser className="text-lg" />}
                  right_icon={
                    <BsFillPatchCheckFill className="text-base text-emerald-500" />
                  }
                  value={userData.user_name}
                  isDisabled
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
